import {ExternalLayerImported} from '@mc/react-mapio';
import {CatalogItemVO, CiselnikVO, LayerAttributeEnumVO, FulltextSearchResultVO, PracovnikEosVO} from '../api';

/**
 * Vypíše naformátovaný název úřední osoby
 * @param pracovnik
 */
export const printFormatterUredniOsobaNazevEOS = (pracovnik: PracovnikEosVO): string => {
    return `${pracovnik.prijmeni1}${pracovnik.jmeno1 ? ' ' + pracovnik.jmeno1 : ''}${pracovnik.titulPred ? `, ` + pracovnik.titulPred : ''}${
        pracovnik.titulZa ? `, ` + pracovnik.titulZa : ''
    }`;
};

/**
 * Vypíše naformátovaný název vyhledané entity
 * @param entita
 */
export const printFormatterFulltextSearchResultVO = (entita: FulltextSearchResultVO): string => {
    switch (entita.type) {
        case 'ORG_JEDNOTKA':
            return `${entita.organizacniJednotka.nazev} (organizační jednotka)`;
        case 'PRACOVNIK':
            return `${printFormatterUredniOsobaNazevEOS(entita.pracovnik)} (uživatel)`;
        case 'ORG_ROLE':
            return `${entita.organizacniRole.nazev} (organizační role)`;
    }
};

/**
 * Vypíše naformátovaný datum
 * @param dateString
 * @param showHour příznak pokud chceme vypsat i hodiny
 * @returns
 */
export const formatDate = (dateString, showHour = true) => {
    if (!dateString) return '';

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based in JavaScript
    const year = date.getFullYear();

    let formattedDate = `${day}.${month}.${year}`;

    if (showHour) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        // const seconds = String(date.getSeconds()).padStart(2, '0');

        formattedDate += ` ${hours}:${minutes}`;
    }

    return formattedDate;
};

export const formatExternalLayersToCatalogItemVO = (externalLayers: ExternalLayerImported[]): CatalogItemVO[] => {
    // Seřadíme vrstvy do skupin podle jejich zdrojového souboru
    const groupedBySourceFilename = externalLayers.reduce((acc: Record<string, any[]>, layer: any) => {
        const key = layer.sourceFileName;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(layer);
        return acc;
    }, {});

    // Transformace skupin na požadovaný formát s podmínkou pro jednotlivé vrstvy nebo skupiny
    const result = [];
    Object.entries(groupedBySourceFilename).forEach(([sourceFileName, layers]) => {
        if (layers.length > 1) {
            // Více vrstev se stejným sourceFileName, vytvoříme skupinu
            result.push({
                id: sourceFileName,
                label: sourceFileName,
                selectedOpen: true,
                children: layers.map((layer: any) => ({
                    layerId: layer.id,
                    label: layer.name !== 'data' ? layer.name : layer.id,
                    id: layer.id,
                    selectedOpen: true,
                })),
            });
        } else {
            // Pouze jedna vrstva se stejným sourceFileName, přidáme samostatně
            const layer = layers[0];
            result.push({
                layerId: layer.id,
                label: layer.name !== 'data' ? layer.name : layer.sourceFileName,
                id: layer.id,
                selectedOpen: true,
            });
        }
    });

    return result;
};

/**
 * Seřadí číselníky (ciselniky) podle názvu abecedně nebo numericky
 * @param ciselniky - seznam číselníků k seřazení
 * @returns seřazený seznam číselníků
 */
export const sortCiselniky = (ciselniky: LayerAttributeEnumVO[] | CiselnikVO[]): any[] => {
    if (!ciselniky || ciselniky.length === 0) {
        return ciselniky;
    }

    return [...ciselniky].sort((a, b) => {
        const aValue = a.nazev || '';
        const bValue = b.nazev || '';

        // Zkusit parsovat jako čísla
        const aNum = Number.parseFloat(aValue);
        const bNum = Number.parseFloat(bValue);

        // Pokud jsou obě hodnoty čísla, seřadit numericky
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            return aNum - bNum;
        }

        // Jinak seřadit abecedně (case-insensitive)
        return aValue.localeCompare(bValue, 'cs', {numeric: true, sensitivity: 'base'});
    });
};

/** Jeden uzel stromu číselníku s hloubkou zanoření (pro odsazené zobrazení). */
export type CiselnikTreeNode = {ciselnik: CiselnikVO; depth: number};

/**
 * Sestaví z plochého seznamu hodnot číselníku odsazený strom podle vazby parentTempId.
 * Hodnoty na každé úrovni jsou seřazeny pomocí {@link sortCiselniky}. Uzly, jejichž rodič
 * nebyl nalezen (nebo nemají parentTempId), jsou kořenové.
 *
 * @param ciselniky - plochý seznam hodnot číselníku
 * @returns seznam uzlů v pořadí pro vykreslení (rodič, pak jeho potomci) s hloubkou zanoření
 */
export const buildCiselnikTreeOrder = (ciselniky?: CiselnikVO[]): CiselnikTreeNode[] => {
    if (!ciselniky || ciselniky.length === 0) {
        return [];
    }

    const knownTempIds = new Set(ciselniky.map(c => c.tempId).filter(Boolean));
    const childrenByParent = new Map<string, CiselnikVO[]>();
    const roots: CiselnikVO[] = [];

    ciselniky.forEach(c => {
        const parentTempId = c.parentTempId;
        if (parentTempId && knownTempIds.has(parentTempId)) {
            const list = childrenByParent.get(parentTempId) ?? [];
            list.push(c);
            childrenByParent.set(parentTempId, list);
        } else {
            roots.push(c);
        }
    });

    const result: CiselnikTreeNode[] = [];
    const visited = new Set<CiselnikVO>();
    const appendLevel = (nodes: CiselnikVO[], depth: number) => {
        (sortCiselniky(nodes) as CiselnikVO[]).forEach(node => {
            if (visited.has(node)) {
                return; // ochrana proti případnému cyklu
            }
            visited.add(node);
            result.push({ciselnik: node, depth});
            const children = node.tempId ? childrenByParent.get(node.tempId) : undefined;
            if (children && children.length > 0) {
                appendLevel(children, depth + 1);
            }
        });
    };
    appendLevel(roots, 0);

    return result;
};

/** Položka stromového výběru nadřazené hodnoty (VF.SelectBox tree); hodnotou je tempId. */
export type CiselnikParentTreeItem = {id: string; nazev: string; children?: CiselnikParentTreeItem[]};

/**
 * Sestaví z hodnot číselníku vnořený strom pro výběr nadřazené hodnoty. Pořadí, řazení i ochranu
 * proti cyklu přebírá z {@link buildCiselnikTreeOrder}, hloubku pak převádí na vnořené `children`.
 * Hodnoty bez tempId se do výběru nedostanou (nelze na ně odkázat).
 *
 * @param ciselniky - plochý seznam hodnot číselníku
 * @returns kořenové položky s vnořenými potomky
 */
export const buildCiselnikParentSelectTree = (ciselniky?: CiselnikVO[]): CiselnikParentTreeItem[] => {
    const roots: CiselnikParentTreeItem[] = [];
    // Položky na aktuální větvi podle hloubky - rodič nové položky je ta o úroveň výš.
    const branch: CiselnikParentTreeItem[] = [];

    buildCiselnikTreeOrder((ciselniky ?? []).filter(c => c.tempId)).forEach(({ciselnik, depth}) => {
        const item: CiselnikParentTreeItem = {id: ciselnik.tempId as string, nazev: ciselnik.nazev ?? ''};
        const parent = depth > 0 ? branch[depth - 1] : undefined;
        if (parent) {
            parent.children = [...(parent.children ?? []), item];
        } else {
            roots.push(item);
        }
        branch[depth] = item;
        branch.length = depth + 1;
    });

    return roots;
};
